/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect } from 'react'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import Select, { SingleValue } from 'react-select'

import { Input, InputGroup } from '@components'
import Button from '@components/Button'
import Modal from '@components/Modal'
import TextEditor from '@components/TextEditor'
import { successMessages } from '@constants/feedbackMessages'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { addAnnouncementSchema } from '@schemas/addAnnouncement.schema'
import { announcementApi } from '@services/AnnouncementApi'
import { closeModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import { getCssStyle, pixelsToRem } from '@utils/helpers/styleHelpers'

type Inputs = {
	type: SingleValue<{ value: string; label: string }>
	name: string
	text: string
}

const styles = {
	container:
		'relative inline-block align-bottom bg-white rounded-lg pl-10 pr-10 pt-6 pb-9 text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle',
	title: 'text-lg leading-6 font-medium text-gray-900',
	subTitle: 'text-gray-500 mt-1',
	buttonGroup: 'mt-7 flex gap-4 justify-end',
}

export default function AddAnnouncement() {
	const dispatch = useAppDispatch()

	const {
		modal: { modalName },
	} = useAppSelector((state) => state)

	const [addAnnouncement, { isLoading, isSuccess }] =
		announcementApi.useAddMutation()

	const {
		register,
		control,
		setValue,
		reset,
		handleSubmit,
		formState: { errors, touchedFields },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addAnnouncementSchema),
	})

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		const datasAnnouncement = {
			type: data?.type?.value,
			name: data.name ? data.name : '',
			text: data.text ? data.text : '',
		}

		addAnnouncement(datasAnnouncement)
		reset()
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(closeModal())
			dispatch(notificationCreators.show(successMessages.storedAnnouncement))
		}
	}, [isSuccess, dispatch])

	return (
		<Modal name='addAnnouncement'>
			<Dialog.Panel
				style={getCssStyle(
					{ name: 'overflowY', value: 'auto' },
					{ name: 'maxWidth', value: pixelsToRem(630) },
					{ name: 'maxHeight', value: pixelsToRem(600) },
					{ name: 'width', value: '100%' }
				)}
				className={`${styles.container} modal-scroll`}
			>
				<p className={styles.title}>Adicionar Comunicado</p>
				<p className={styles.subTitle}>
					Preencha os campos abaixo para adicionar um novo comunicado
				</p>

				<form onSubmit={handleSubmit(onSubmit)}>
					<InputGroup label='Tipo' id='type' className='w-full mt-6'>
						<Controller
							control={control}
							name='type'
							render={({
								field: { value, onBlur },
								fieldState: { isTouched, error },
							}) => {
								return (
									<Select
										onChange={(v) => setValue('type', v)}
										options={[
											{
												value: 'E-mail',
												label: 'E-mail',
											},
											{
												value: 'Quadro de Aviso',
												label: 'Quadro de Aviso',
											},
										]}
										value={value}
										onBlur={onBlur}
										styles={{
											input: (base) => ({
												...base,
												borderRadius: '6px',
												paddingLeft: '2.5px',
												paddingRight: '2.5px',
												paddingTop: '3.5px',
												paddingBottom: '3.5px',
											}),
											control: (controlStyles) => ({
												...controlStyles,
												borderColor: (() => {
													const isValid = inputIsValid(error, isTouched)

													if (typeof isValid === 'undefined') {
														return '#D1D5DB !important'
													}

													if (!isValid) {
														return '#F44545 !important'
													}

													return '#15CA71 !important'
												})(),
											}),
											option: (optionStyle, { isFocused }) => ({
												...optionStyle,
												backgroundColor: isFocused ? '#1673FA' : '#fff',
												color: isFocused ? '#fff' : '#000',
											}),
										}}
										placeholder=''
										classNamePrefix='react-select'
									/>
								)
							}}
						/>
					</InputGroup>

					<InputGroup className='mt-4' label='Nome do comunicado' id='name'>
						<Input
							isValid={inputIsValid(errors.name, touchedFields.name)}
							{...register('name')}
							type='text'
						/>
					</InputGroup>

					<Controller
						{...register('text')}
						name='text'
						control={control}
						render={({ field: { onChange, onBlur } }) => (
							<InputGroup
								className={`mt-6 w-[100%] index-0-controler ${
									errors.text && 'error-textBox'
								}`}
								label='Texto'
								id='text'
							>
								<TextEditor onChange={onChange} onBlur={onBlur} />
							</InputGroup>
						)}
					/>

					<div className={styles.buttonGroup}>
						<Button
							type='button'
							onClick={() => {
								dispatch(closeModal())
							}}
							variant='outline'
							disabled={false}
						>
							Cancelar
						</Button>

						<Button type='submit' isLoading={isLoading} disabled={!modalName}>
							Adicionar Comunicado
						</Button>
					</div>
				</form>
			</Dialog.Panel>
		</Modal>
	)
}
